import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HelpCircle } from "lucide-react";

const DISMISS_KEY = "kadana_prompt_lang_help_seen";

// Patch 47 Part J2-J4: prompting-language toggle on the hero prompt console. Lists all 16 (15 full UI
// languages + Sheng, visibly marked "prompt input only"). First non-English pick shows a one-time
// disclosure; a "?" always reopens it. "Think Global, App Local." sits beneath as a caption.
export default function PromptLangPicker() {
  const [langs, setLangs] = useState([]);
  const [sel, setSel] = useState("en");
  const [help, setHelp] = useState(false);

  useEffect(() => {
    api.get("/public/prompt-languages").then((r) => setLangs(r.data.languages || [])).catch(() => {});
  }, []);

  const pick = (code) => {
    setSel(code);
    const lang = langs.find((l) => l.code === code);
    const box = document.querySelector('[data-testid="hero-prompt"]');
    if (box && lang && lang.ph) box.setAttribute("placeholder", lang.ph);
    if (code !== "en" && localStorage.getItem(DISMISS_KEY) !== "1") {
      setHelp(true);
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    }
  };

  return (
    <div data-testid="prompt-lang-picker" style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="k-muted" style={{ fontSize: 12 }}>Prompt in:</span>
        <select className="k-control" data-testid="prompt-lang-select" value={sel}
          onChange={(e) => pick(e.target.value)} style={{ width: "auto", fontSize: 13, padding: "6px 8px" }}>
          {langs.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}{l.endonym && l.endonym !== l.name ? ` · ${l.endonym}` : ""}
              {l.tier === "input" ? " (prompt input only)" : ""}
            </option>
          ))}
        </select>
        <button type="button" className="k-btn k-btn-ghost" data-testid="prompt-lang-help-btn"
          aria-label="About prompting languages" onClick={() => setHelp((h) => !h)}
          style={{ padding: "5px 8px", minHeight: 0 }}><HelpCircle size={15} /></button>
      </div>

      {help && (
        <div className="k-container k-fadein" data-testid="prompt-lang-help" style={{ padding: 14, marginTop: 8 }}>
          <p className="k-muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.6 }}>
            Prompting in your own language is fully supported: the Coach will show <strong>“I understood
            this as: …”</strong> in your chosen language before building anything. For the 15 full UI
            languages, your generated app’s own interface can also be delivered in that language.
          </p>
          <p className="k-muted" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
            The exception is <strong>Sheng</strong>, which is understood as prompt input and confirmed back
            to you — but a generated app’s interface is delivered in English or your other chosen UI language,
            never in Sheng itself, because Sheng has no standardised written form to build a reliable interface in.
          </p>
          <button className="k-btn k-btn-ghost" data-testid="prompt-lang-help-close"
            onClick={() => setHelp(false)} style={{ marginTop: 8, fontSize: 12, padding: "4px 10px" }}>Got it</button>
        </div>
      )}
    </div>
  );
}
